import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { ActionButton, RadioGroupItem, Spinner } from '@communityshare/ui-shared';
import { HOME_VIDEO_AUDIENCES, HomeVideoAudiences } from '@/features/videos-common/audiences';
import { usePlaylistImport, usePlaylistImportPreview } from '../hooks/useStaffPlaylists';
import useSingleFlight from '../hooks/useSingleFlight';
import getErrorMessage from '../api/errorMessages';
import FeatureDialog from './FeatureDialog';
import FormRow, { inputClassName } from './FormRow';
import StatusMessage from './StatusMessage';

const AUDIENCE_OPTIONS = [null, ...HOME_VIDEO_AUDIENCES];
const TAGGING_AUDIENCES = [HomeVideoAudiences.Educator, HomeVideoAudiences.Partner];

/**
 * Import a YouTube playlist in two steps: Preview (writes nothing), then
 * Import. Backend rejections (auto mixes, private playlists, ...) are shown
 * as the server worded them. Imported videos arrive as unpublished drafts.
 *
 * An optional audience (default None) is sent only when chosen. If another
 * playlist already holds it, the preview names that playlist and Import is
 * disabled. Educator and Partner imports tag the new videos; Both and None
 * imports leave them untagged.
 */
ImportPlaylistDialog.propTypes = {
  onClose: PropTypes.func.isRequired
};

export default function ImportPlaylistDialog(props) {
  const { onClose } = props;
  const { t } = useTranslation();

  const [url, setUrl] = useState('');
  const [audience, setAudience] = useState(null);
  const [urlError, setUrlError] = useState(null);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const previewMutation = usePlaylistImportPreview();
  const importMutation = usePlaylistImport();
  const urlRef = useRef(null);
  const resultRef = useRef(null);

  const importing = importMutation.isLoading;
  const busy = previewMutation.isLoading || importing;

  function handleAudienceChange(value) {
    setAudience(value);
    // The preview (and its conflict check) belongs to the audience it was made for.
    setPreview(null);
    setError(null);
  }

  function handleUrlChange(value) {
    setUrl(value);
    setUrlError(null);
    // The preview belongs to the URL it was made for.
    setPreview(null);
    setError(null);
  }

  const handlePreview = useSingleFlight(async () => {
    setError(null);
    setPreview(null);
    if (!url.trim()) {
      setUrlError(t('staff_videos.import.url_required'));
      urlRef.current?.focus();
      return;
    }
    try {
      setPreview(await previewMutation.mutateAsync({ url, audience }));
    } catch (requestError) {
      setError(getErrorMessage(requestError, t, 'playlist'));
    }
  });

  const handleImport = useSingleFlight(async () => {
    setError(null);
    try {
      const imported = await importMutation.mutateAsync({ url, audience });
      setResult({ ...imported, audience });
      // The form unmounts; move focus to the success panel once it renders.
      setTimeout(() => resultRef.current?.focus(), 0);
    } catch (requestError) {
      setError(getErrorMessage(requestError, t, 'playlist'));
    }
  });

  function startOver() {
    setUrl('');
    setAudience(null);
    setPreview(null);
    setResult(null);
    setError(null);
    setTimeout(() => urlRef.current?.focus(), 0);
  }

  const newCount = preview ? preview.newVideos.length : 0;
  const conflict = preview?.audienceConflict || null;
  // With no new videos, importing still assigns the chosen audience.
  const canImport = Boolean(preview) && (newCount > 0 || Boolean(audience));
  const audienceLabel = (value) => t(`staff_videos.audiences.${value || 'none'}`);

  function successMessage() {
    const playlist = result.playlist?.title ?? '';
    if (result.createdCount === 0) {
      return t('staff_videos.import.success_none', { playlist });
    }
    return t('staff_videos.import.success', { count: result.createdCount, playlist });
  }

  function taggingMessage() {
    if (!result.createdCount) return null;
    return TAGGING_AUDIENCES.includes(result.audience)
      ? t('staff_videos.import.success_tagged', { audience: audienceLabel(result.audience) })
      : t('staff_videos.import.success_untagged');
  }

  return (
    <FeatureDialog
      title={t('staff_videos.import.title')}
      onClose={importing ? () => {} : onClose}
      initialFocus={urlRef}
    >
      {result ? (
        <div ref={resultRef} tabIndex={-1} className="mt-6 space-y-6 focus:outline-none">
          <div aria-live="polite">
            <StatusMessage tone="success" title={successMessage()} detail={taggingMessage()} />
          </div>
          <div className="flex flex-col-reverse md:flex-row md:justify-between gap-3">
            <ActionButton variant="secondary" onClick={startOver}>
              {t('staff_videos.import.start_over')}
            </ActionButton>
            <ActionButton variant="primary" onClick={onClose}>
              {t('staff_videos.import.done')}
            </ActionButton>
          </div>
        </div>
      ) : (
        <form
          noValidate
          className="mt-6 space-y-5"
          onSubmit={(event) => {
            event.preventDefault();
            handlePreview();
          }}
        >
          <FormRow
            id="staff-playlist-url"
            label={t('staff_videos.import.url')}
            required
            help={t('staff_videos.import.url_help')}
            error={urlError}
          >
            {(aria) => (
              <div className="flex flex-col sm:flex-row gap-2">
                <input
                  {...aria}
                  ref={urlRef}
                  type="url"
                  inputMode="url"
                  autoComplete="off"
                  value={url}
                  onChange={(event) => handleUrlChange(event.target.value)}
                  disabled={busy}
                  className={inputClassName(Boolean(urlError))}
                />
                <ActionButton
                  type="submit"
                  variant="secondary"
                  loading={previewMutation.isLoading}
                  loadingLabel={t('staff_videos.import.previewing')}
                  disabled={busy}
                >
                  {t('staff_videos.import.preview')}
                </ActionButton>
              </div>
            )}
          </FormRow>

          <div className="space-y-2">
            <p id="staff-playlist-audience-label" className="text-sm font-semibold text-cs-gray-900">
              {t('staff_videos.import.audience')}
            </p>
            <p id="staff-playlist-audience-help" className="text-sm text-cs-gray-500">
              {t('staff_videos.import.audience_help')}
            </p>
            <div
              role="radiogroup"
              aria-labelledby="staff-playlist-audience-label"
              aria-describedby="staff-playlist-audience-help"
              className="flex flex-wrap gap-2"
            >
              {AUDIENCE_OPTIONS.map((option) => (
                <RadioGroupItem
                  key={option || 'none'}
                  label={audienceLabel(option)}
                  selected={audience === option}
                  disabled={busy}
                  onChange={() => handleAudienceChange(option)}
                />
              ))}
            </div>
          </div>

          <div aria-live="polite" className="space-y-4">
            {error && (
              <StatusMessage tone="error" title={error.message} detail={error.detail} />
            )}

            {preview && (
              <div className="bg-cs-gray-50 rounded-cs-10 p-4 space-y-3" data-testid="staff-playlist-preview">
                <dl className="grid grid-cols-[auto,1fr] gap-x-4 gap-y-2 text-sm">
                  <dt className="font-semibold text-cs-gray-900">{t('staff_videos.import.playlist_title')}</dt>
                  <dd className="text-cs-gray-700">{preview.playlistTitle}</dd>
                  <dt className="font-semibold text-cs-gray-900">{t('staff_videos.import.item_count')}</dt>
                  <dd className="text-cs-gray-700 tabular-nums">{preview.itemCount}</dd>
                  <dt className="font-semibold text-cs-gray-900">{t('staff_videos.import.new_count')}</dt>
                  <dd className="text-cs-gray-700 tabular-nums">{newCount}</dd>
                  <dt className="font-semibold text-cs-gray-900">{t('staff_videos.import.existing_count')}</dt>
                  <dd className="text-cs-gray-700 tabular-nums">{preview.existingCount}</dd>
                  <dt className="font-semibold text-cs-gray-900">{t('staff_videos.import.audience_row')}</dt>
                  <dd className="text-cs-gray-700">{audienceLabel(audience)}</dd>
                </dl>
                <p className="text-sm text-cs-gray-700">
                  {newCount > 0 ? t('staff_videos.import.drafts_notice') : t('staff_videos.import.nothing_new')}
                </p>
              </div>
            )}

            {conflict && (
              <div id="staff-playlist-conflict">
                <StatusMessage
                  tone="warning"
                  title={t('staff_videos.import.conflict', {
                    audience: audienceLabel(audience),
                    holder: conflict.title
                  })}
                />
              </div>
            )}

            {importing && (
              <p className="flex items-center gap-2 text-sm text-cs-gray-700">
                <Spinner />
                {t('staff_videos.import.importing')}
              </p>
            )}
          </div>

          <div className="flex flex-col-reverse md:flex-row md:justify-between gap-3 pt-2">
            <ActionButton variant="secondary" onClick={onClose} disabled={importing}>
              {t('staff_videos.import.cancel')}
            </ActionButton>
            {canImport && (
              <ActionButton
                variant="primary"
                loading={importing}
                loadingLabel={t('staff_videos.import.importing')}
                disabled={busy || Boolean(conflict)}
                aria-describedby={conflict ? 'staff-playlist-conflict' : undefined}
                onClick={handleImport}
              >
                {newCount > 0
                  ? t('staff_videos.import.confirm', { count: newCount })
                  : t('staff_videos.import.confirm_playlist')}
              </ActionButton>
            )}
          </div>
        </form>
      )}
    </FeatureDialog>
  );
}
